import { FILE_EXTENSION_ALLOWED } from "config";
import { useBucketContext } from "context/bucket";
import { ChangeEvent, MouseEvent, useCallback, useRef } from "react";
import { Button } from "react-bootstrap";
import { showErrorModal, showSummaryModal } from "redux/slices";
import { useAppDispatch } from "redux/store";
import { showSuccessToast } from "utils";
import { UploadFileResult } from "./types";

export const AddFile = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { uploadFiles, checkFileExist, refreshContents, apiHandler } =
    useBucketContext();
  const dispatch = useAppDispatch();

  const handleResult = useCallback(
    (uploadResult: UploadFileResult) => {
      const { successFilesUploaded, failureFilesUploaded, existingFiles } =
        uploadResult;
      // all good case
      if (
        successFilesUploaded.length &&
        !failureFilesUploaded.length &&
        !existingFiles.length
      ) {
        showSuccessToast(
          `File caricat${successFilesUploaded.length > 1 ? "i" : "o"}`,
          "success_files_upload"
        );
      }
      // all wrong case
      else if (
        !successFilesUploaded.length &&
        failureFilesUploaded.length &&
        !existingFiles.length
      ) {
        dispatch(showErrorModal({ text: "Errore in caricamento dei file" }));
      } else {
        dispatch(
          showSummaryModal({
            type: "error",
            title: "Oh oh",
            successListItem: successFilesUploaded.map(
              (o) => `${o.name}: caricato con successo`
            ),
            failureListItem: [
              ...failureFilesUploaded.map(
                (o) => `${o.name}: errore nel caricamento`
              ),
              ...existingFiles.map((o) => `${o.name}: file già esistente`),
            ],
          })
        );
      }
    },
    [dispatch]
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    inputRef?.current?.click();
  };

  const handleAcceptedFiles = useCallback(
    async (files: File[]): Promise<UploadFileResult> => {
      const existingFiles: File[] = [];
      const newFiles: File[] = [];

      for (let file of files) {
        const exist = await checkFileExist?.(file);
        exist ? existingFiles.push(file) : newFiles.push(file);
      }

      const result = await uploadFiles?.(newFiles);
      if (result) {
        return { ...result, existingFiles };
      } else {
        return {
          successFilesUploaded: [],
          failureFilesUploaded: [],
          existingFiles,
        };
      }
    },
    [checkFileExist, uploadFiles]
  );

  const uploadAction = async (files: File[]) => {
    const result = await handleAcceptedFiles(files);
    if (result) {
      handleResult(result);
      result?.successFilesUploaded.length > 0 && (await refreshContents());
    }
  };

  const handleFileChange = async (fileItemsList: FileList | null) => {
    const total = fileItemsList?.length ?? 0;
    if (!total) {
      return;
    }
    let count = 0;
    const files: File[] = [];
    while (count < total) {
      const file = fileItemsList?.item(count);
      file && files.push(file);
      count++;
    }
    await apiHandler(async () => await uploadAction(files));
  };

  const onInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    await handleFileChange(event?.target?.files);
    event.target.value = "";
  };

  return (
    <>
      <input
        className="d-none"
        ref={inputRef}
        type="file"
        multiple
        accept={FILE_EXTENSION_ALLOWED.join(", ")}
        onChange={onInputChange}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        value={undefined}
      />
      <Button
        onClick={handleClick}
        variant="secondary text-white text-nowrap fw-light"
        className="d-flex align-items-center gap-2 fs-sm-12"
      >
        <i className="bi bi-file-earmark" />
        <span>CARICA FILE</span>
      </Button>
    </>
  );
};
